import { SVGAssets } from '@business/assets/vectors';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../Button';

interface UploadProgressModalProps {
  isOpen: boolean;
  current: number;
  total: number;
  onClose: () => void;
}

type AnimationPhase = 'idle' | 'creeping' | 'completing' | 'done';

export const UploadProgressModal: React.FC<UploadProgressModalProps> = ({
  isOpen,
  current,
  total,
  onClose
}) => {
  const { t } = useTranslation();

  // `visible` stays true a bit longer than `isOpen` so the completion
  // animation has time to play before the modal unmounts.
  const [visible, setVisible] = useState(false);
  const [displayProgress, setDisplayProgress] = useState(0);

  const phaseRef = useRef<AnimationPhase>('idle');
  const stopTargetRef = useRef(0);
  const completingTargetRef = useRef(0);
  const currentRef = useRef(current);
  const totalRef = useRef(total);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingCloseAfterCompleteRef = useRef(false);

  // Keep refs in sync with latest props inside the interval closure.
  useEffect(() => {
    currentRef.current = current;
  }, [current]);
  useEffect(() => {
    totalRef.current = total;
  }, [total]);

  // Open / close visibility with a delayed-close guard.
  useEffect(() => {
    if (isOpen) {
      if (closeTimerRef.current) {
        clearTimeout(closeTimerRef.current);
        closeTimerRef.current = null;
      }
      pendingCloseAfterCompleteRef.current = false;
      setVisible(true);
    } else {
      if (visible && displayProgress > 0) {
        // Parent may reset current/total immediately; force final fill to 100% first.
        completingTargetRef.current = 100;
        phaseRef.current = 'completing';
        pendingCloseAfterCompleteRef.current = true;
      } else {
        // For cancellations/early exits, keep a short delayed close.
        closeTimerRef.current = setTimeout(() => {
          setVisible(false);
          // Full reset for next usage.
          phaseRef.current = 'idle';
          stopTargetRef.current = 0;
          completingTargetRef.current = 0;
          pendingCloseAfterCompleteRef.current = false;
          setDisplayProgress(0);
        }, 900);
      }
    }
    return () => {
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    };
  }, [isOpen, visible, displayProgress]);

  // Close only after the visual bar has effectively reached 100%.
  useEffect(() => {
    if (!visible || !pendingCloseAfterCompleteRef.current) return undefined;
    if (displayProgress < 99.9) return undefined;

    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
    }

    closeTimerRef.current = setTimeout(() => {
      setVisible(false);
      phaseRef.current = 'idle';
      stopTargetRef.current = 0;
      completingTargetRef.current = 0;
      pendingCloseAfterCompleteRef.current = false;
      setDisplayProgress(0);
    }, 180);

    return () => {
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    };
  }, [displayProgress, visible]);

  // When the modal becomes visible with a known total, kick off the first creep.
  useEffect(() => {
    if (!visible || total === 0) return;
    const slotSize = 100 / total;
    stopTargetRef.current = Math.max(slotSize * (0.3 + Math.random() * 0.4), 2);
    phaseRef.current = 'creeping';
    setDisplayProgress(0);
  }, [visible, total]);

  // When a new item completes, immediately switch to completing phase.
  useEffect(() => {
    if (!visible || current === 0 || total === 0) return;
    completingTargetRef.current = (current / total) * 100;
    phaseRef.current = 'completing';
  }, [current]);

  // Single animation interval that drives both phases.
  useEffect(() => {
    if (!visible) return undefined;

    const id = setInterval(() => {
      setDisplayProgress(prev => {
        const phase = phaseRef.current;

        if (phase === 'creeping') {
          const target = stopTargetRef.current;
          if (prev >= target - 0.05) return prev; // paused — waiting for upload
          return Math.min(prev + 0.25, target);
        }

        if (phase === 'completing') {
          const target = completingTargetRef.current;
          const next = Math.min(prev + 0.8, target);

          if (next >= target - 0.05) {
            const c = currentRef.current;
            const t2 = totalRef.current;

            if (c < t2) {
              // More items remain — creep toward a random stop in the next slot.
              const slotSize = 100 / t2;
              const minStop = target + slotSize * 0.3;
              const maxStop = target + slotSize * 0.7;
              stopTargetRef.current =
                minStop + Math.random() * (maxStop - minStop);
              phaseRef.current = 'creeping';
            } else {
              // All done — bar reaches 100 %.
              phaseRef.current = 'done';
            }

            return target;
          }

          return next;
        }

        // 'idle' / 'done' — bar stays put; close timer will unmount shortly.
        return prev;
      });
    }, 40);

    return () => clearInterval(id);
  }, [visible]);

  if (!visible) return null;

  return (
    <div className="fixed inset-0 bg-[#353535] bg-opacity-50 flex items-center justify-center z-50 ">
      <div className="bg-white rounded-lg p-6 w-96 shadow-xl confirmation-box-style">
        <div className="flex justify-between items-center mb-6 border-b">
          <h3 className="text-lg font-semibold">
            {t('dashboard.uploadFiles')}
          </h3>
          <Button
            btnStyle="link"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
            data-testid="close-button">
            <SVGAssets.CloseIconGray className="w-5 h-4 text-gray-400" />
          </Button>
        </div>

        <div className="mb-6">
          <p className="text-center text-sm font-bold mb-4">
            {t('dashboard.imagesBeingUploaded')}
          </p>

          <div className="w-full bg-gray-200 rounded-full h-2.5 mb-3">
            <div
              className="bg-[#49712B] h-2.5 rounded-full"
              style={{
                width: `${displayProgress}%`,
                transition: 'width 80ms linear'
              }}
            />
          </div>

          <p className="text-center text-xs text-gray-600">
            {t('dashboard.imagesUploadedCount', { current, total })}
          </p>
        </div>

        <div className="flex justify-end">
          <Button
            btnStyle="primary"
            onClick={onClose}
            className="px-4 py-2 text-sm rounded-md">
            {t('cancel')}
          </Button>
        </div>
      </div>
    </div>
  );
};
